import * as service from './service'
import { me } from '../auth/service'
import notification from '@components/notification'
import { capitalize, getMessages } from '@helpers/index'
import { GOOGLE_CONNECTED_ALREADY_ERROR_MSG } from '@helpers/constants'
import { setActiveTotalAccounts } from '@helpers/utility'
import i18n from '@/i18n'

// Helper function to handle multi-platform errors
// Extracted outside model to avoid 'this' context issues in reducers
function handleMultiPlatformErrorHelper(state, payload) {
  console.log('[manage_business.handleMultiPlatformError] ========== START HANDLE MULTI PLATFORM ERROR ==========')
  console.log('[manage_business.handleMultiPlatformError] Payload received:', JSON.stringify(payload, null, 2))
  console.log('[manage_business.handleMultiPlatformError] Payload type:', typeof payload)
  console.log('[manage_business.handleMultiPlatformError] Payload keys:', payload ? Object.keys(payload) : 'null')
  console.log('[manage_business.handleMultiPlatformError] Payload structure:', {
    hasSummary: !!payload.summary,
    hasErrorType: !!payload.errorType,
    errorType: payload.errorType,
    hasPlatforms: !!payload.platforms,
    hasFailedPlatforms: !!payload.failedPlatforms,
    hasSucceededPlatforms: !!payload.succeededPlatforms,
    failedPlatforms: payload.failedPlatforms,
    succeededPlatforms: payload.succeededPlatforms,
    platformsKeys: payload.platforms ? Object.keys(payload.platforms) : []
  })
  console.log('[manage_business.handleMultiPlatformError] Current state:', {
    loading: state.loading,
    apiError: state.apiError,
    hasMultiPlatformModal: !!state.multiPlatformErrorModal,
    syncWithThirdPartiesSynchronously: state.syncWithThirdPartiesSynchronously
  })

  // Normalize platform names to uppercase for consistency
  const normalizePlatformName = (name) => {
    if (!name) return name
    const upper = name.toUpperCase()
    return upper === 'GOOGLE' || upper === 'FACEBOOK' ? upper : name
  }

  const failedPlatforms = (payload.failedPlatforms || []).map(normalizePlatformName)
  const succeededPlatforms = (payload.succeededPlatforms || []).map(normalizePlatformName)

  // Transform errors array to platforms object if platforms is missing or empty
  let platforms = payload.platforms || {}
  const platformsIsEmpty = !platforms || Object.keys(platforms).length === 0
  const hasErrorsArray = payload.errors && Array.isArray(payload.errors) && payload.errors.length > 0

  console.log('[manage_business.handleMultiPlatformError] Platforms check:', {
    hasPlatforms: !!payload.platforms,
    platformsIsEmpty,
    platformsKeys: platforms ? Object.keys(platforms) : [],
    hasErrorsArray,
    errorsCount: payload.errors ? payload.errors.length : 0
  })

  if (platformsIsEmpty && hasErrorsArray) {
    console.log('[manage_business.handleMultiPlatformError] ========== TRANSFORMING ERRORS TO PLATFORMS ==========')
    console.log('[manage_business.handleMultiPlatformError] Errors array:', JSON.stringify(payload.errors, null, 2))
    platforms = {}

    // Process errors array to build platforms object
    payload.errors.forEach((error) => {
      const platformName = normalizePlatformName(error.platform)
      if (platformName) {
        platforms[platformName] = {
          platform: platformName,
          status: 'FAILED',
          errorMessage: error.message || (error.details && error.details.summary) || 'Sync failed',
          errorDetails: error.details || error.errorDetails || null,
          canDisconnect: error.canDisconnect !== false
        }
        console.log(
          `[manage_business.handleMultiPlatformError] Added failed platform ${platformName} from errors array`
        )
      }
    })

    // Add succeeded platforms
    succeededPlatforms.forEach((platformName) => {
      if (!platforms[platformName]) {
        platforms[platformName] = {
          platform: platformName,
          status: 'SUCCESS',
          canDisconnect: true
        }
        console.log(`[manage_business.handleMultiPlatformError] Added succeeded platform ${platformName}`)
      }
    })

    // Add any failed platforms that weren't in errors array
    failedPlatforms.forEach((platformName) => {
      if (!platforms[platformName]) {
        platforms[platformName] = {
          platform: platformName,
          status: 'FAILED',
          errorMessage: 'Sync failed',
          errorDetails: null,
          canDisconnect: true
        }
        console.log(
          `[manage_business.handleMultiPlatformError] Added failed platform ${platformName} without error details`
        )
      }
    })

    console.log('[manage_business.handleMultiPlatformError] Transformed platforms:', Object.keys(platforms))
  }

  console.log('[manage_business.handleMultiPlatformError] Normalized platforms:', {
    failedPlatforms,
    succeededPlatforms,
    platformsKeys: Object.keys(platforms),
    platformsCount: Object.keys(platforms).length
  })

  // Normalize platform error details and keys to uppercase
  console.log('[manage_business.handleMultiPlatformError] ========== PROCESSING PLATFORMS ==========')
  const normalizedPlatforms = {}
  Object.keys(platforms).forEach((platformKey) => {
    const platform = platforms[platformKey]
    const normalizedKey = normalizePlatformName(platformKey)
    console.log(`[manage_business.handleMultiPlatformError] Processing platform: ${platformKey} -> ${normalizedKey}`)
    console.log(
      `[manage_business.handleMultiPlatformError] Platform ${platformKey} data:`,
      JSON.stringify(platform, null, 2)
    )
    console.log(`[manage_business.handleMultiPlatformError] Platform ${platformKey} structure:`, {
      status: platform.status,
      hasErrorMessage: !!platform.errorMessage,
      errorMessage: platform.errorMessage,
      hasErrorDetails: !!platform.errorDetails,
      errorDetailsKeys: platform.errorDetails ? Object.keys(platform.errorDetails) : [],
      hasIssues: !!(platform.errorDetails && platform.errorDetails.issues),
      issuesCount: platform.errorDetails && platform.errorDetails.issues ? platform.errorDetails.issues.length : 0,
      canDisconnect: platform.canDisconnect
    })
    normalizedPlatforms[normalizedKey] = {
      platform: normalizePlatformName(platform.platform || platformKey),
      status: platform.status,
      errorMessage: platform.errorMessage,
      errorDetails: platform.errorDetails,
      canDisconnect: platform.canDisconnect !== false
    }
    console.log(
      `[manage_business.handleMultiPlatformError] Normalized platform ${normalizedKey}:`,
      JSON.stringify(normalizedPlatforms[normalizedKey], null, 2)
    )
  })
  console.log('[manage_business.handleMultiPlatformError] All normalized platforms:', Object.keys(normalizedPlatforms))

  const modalState = {
    visible: true,
    summary: payload.summary || 'One or more social platforms failed to sync your business information.',
    platforms: normalizedPlatforms,
    failedPlatforms,
    succeededPlatforms,
    partialSuccess: payload.partialSuccess || false,
    showDetails: false
  }

  console.log('[manage_business.handleMultiPlatformError] Created modal state:', JSON.stringify(modalState, null, 2))
  console.log('[manage_business.handleMultiPlatformError] Modal visible:', modalState.visible)
  console.log('[manage_business.handleMultiPlatformError] Platforms in modal:', Object.keys(modalState.platforms))
  console.log(
    '[manage_business.handleMultiPlatformError] Failed platforms with error details:',
    failedPlatforms.map((p) => ({
      platform: p,
      hasErrorDetails: !!(normalizedPlatforms[p] && normalizedPlatforms[p].errorDetails),
      hasIssues: !!(
        normalizedPlatforms[p] &&
        normalizedPlatforms[p].errorDetails &&
        normalizedPlatforms[p].errorDetails.issues
      ),
      issuesCount:
        (normalizedPlatforms[p] &&
          normalizedPlatforms[p].errorDetails &&
          normalizedPlatforms[p].errorDetails.issues &&
          normalizedPlatforms[p].errorDetails.issues.length) ||
        0
    }))
  )

  // Verify we have error details to show
  console.log('[manage_business.handleMultiPlatformError] ========== VERIFYING ERROR DETAILS ==========')
  const hasErrorDetails = Object.values(normalizedPlatforms).some(
    (platform) => platform.status === 'FAILED' && platform.errorDetails
  )
  console.log('[manage_business.handleMultiPlatformError] Verification results:', {
    hasErrorDetails,
    failedPlatformsCount: failedPlatforms.length,
    failedPlatforms,
    platformsWithErrors: Object.values(normalizedPlatforms)
      .filter((p) => p.status === 'FAILED' && p.errorDetails)
      .map((p) => p.platform)
  })

  if (!hasErrorDetails && failedPlatforms.length === 0) {
    console.warn('[manage_business.handleMultiPlatformError] ========== VALIDATION FAILED ==========')
    console.warn(
      '[manage_business.handleMultiPlatformError] No failed platforms or error details found, cannot show modal'
    )
    console.warn('[manage_business.handleMultiPlatformError] Returning state without modal')
    return {
      ...state,
      loading: false,
      apiError: true
    }
  }

  // Don't show toaster notification - show modal instead
  console.log('[manage_business.handleMultiPlatformError] ========== CREATING FINAL STATE ==========')
  const newState = {
    ...state,
    loading: false,
    apiError: true,
    multiPlatformErrorModal: modalState,
    gmbErrorModal: null,
    facebookErrorModal: null
  }

  console.log('[manage_business.handleMultiPlatformError] ========== FINAL STATE CREATED ==========')
  console.log(
    '[manage_business.handleMultiPlatformError] New state multiPlatformErrorModal:',
    JSON.stringify(newState.multiPlatformErrorModal, null, 2)
  )
  console.log(
    '[manage_business.handleMultiPlatformError] Modal will be visible:',
    newState.multiPlatformErrorModal.visible
  )
  console.log(
    '[manage_business.handleMultiPlatformError] Modal has platforms:',
    Object.keys(newState.multiPlatformErrorModal.platforms).length
  )
  console.log(
    '[manage_business.handleMultiPlatformError] Modal platforms keys:',
    Object.keys(newState.multiPlatformErrorModal.platforms)
  )
  console.log(
    '[manage_business.handleMultiPlatformError] Modal failed platforms:',
    newState.multiPlatformErrorModal.failedPlatforms
  )
  console.log(
    '[manage_business.handleMultiPlatformError] Modal succeeded platforms:',
    newState.multiPlatformErrorModal.succeededPlatforms
  )
  console.log('[manage_business.handleMultiPlatformError] ========== END HANDLE MULTI PLATFORM ERROR ==========')
  console.log('[manage_business.handleMultiPlatformError] Returning new state with modal')
  return newState
}

export default {
  state: {
    staffs: [],
    fields: [],
    activityList: [],
    primaryCategories: [],
    boardingCategories: [],
    boardingCategoriesOther: [],
    secondaryCategories: [],
    loading: true,
    gmbData: [],
    apiError: false,
    selectedSocialDetails: [],
    facebookData: '',
    selectedFacebookPage: {},
    goalsList: '',
    isGbmEnabled: null,
    isGmbActive: null,
    editField: null,
    gmbDataOfUser: {},
    gmbErrorModal: null,
    multiPlatformErrorModal: null,
    facebookErrorModal: null,
    syncWithThirdPartiesSynchronously: false
  },
  reducers: {
    setCallForwardPhone(state, callForwardPhone) {
      return {
        ...state,
        businessSettings: {
          ...state.businessSettings,
          callForwardPhone: callForwardPhone || ''
        }
      }
    },
    onRequest(state) {
      return {
        ...state,
        loading: true,
        apiError: false
      }
    },
    onSuccess(state, data, msg) {
      if (msg) {
        notification('success', msg)
      }
      return {
        ...state,
        loading: false
      }
    },
    onError(state, data) {
      console.log('[manage_business.onError] ========== ONERROR CALLED ==========')
      console.log('[manage_business.onError] Input data:', JSON.stringify(data, null, 2))
      console.log('[manage_business.onError] Input data type:', typeof data)
      console.log('[manage_business.onError] Input data keys:', data ? Object.keys(data) : 'null')

      const isSyncEnabled = Boolean(state.syncWithThirdPartiesSynchronously)
      console.log('[manage_business.onError] syncWithThirdPartiesSynchronously =', isSyncEnabled)
      console.log('[manage_business.onError] Current state:', {
        loading: state.loading,
        apiError: state.apiError,
        hasMultiPlatformModal: !!state.multiPlatformErrorModal
      })

      // Extract error payload from various possible locations
      // Axios error structure: error.response.data contains the actual response body
      // Also handle case where updateBusinessSettings passes { data: res } for error-like success responses
      const possiblePayloads = [
        data && data.response && data.response.data, // Axios error response (most common for 400/500 errors)
        data && data.data, // When updateBusinessSettings passes { data: res }
        data && data.body,
        data // Direct payload
      ].filter(Boolean)

      console.log('[manage_business.onError] ========== PAYLOAD EXTRACTION ==========')
      console.log('[manage_business.onError] Possible payloads count:', possiblePayloads.length)
      possiblePayloads.forEach((payload, index) => {
        console.log(`[manage_business.onError] Payload ${index + 1}:`, {
          type: typeof payload,
          keys: payload ? Object.keys(payload) : 'null',
          hasErrorType: !!(payload && payload.errorType),
          errorType: payload && payload.errorType,
          hasPlatforms: !!(payload && payload.platforms),
          hasFailedPlatforms: !!(payload && payload.failedPlatforms),
          hasSucceededPlatforms: !!(payload && payload.succeededPlatforms),
          payload: JSON.stringify(payload, null, 2)
        })
      })

      // Find payload with errorType (single source of truth)
      let errorPayload = possiblePayloads.find((payload) => payload && payload.errorType)

      console.log('[manage_business.onError] First errorPayload search result:', errorPayload)

      // If still not found, check if any payload has the MULTI_PLATFORM_SYNC_FAILED structure
      if (!errorPayload) {
        console.log('[manage_business.onError] errorPayload not found by errorType, checking structure...')
        errorPayload = possiblePayloads.find(
          (payload) =>
            payload &&
            (payload.errorType === 'MULTI_PLATFORM_SYNC_FAILED' ||
              (payload.platforms && (payload.failedPlatforms || payload.succeededPlatforms)))
        )
        console.log('[manage_business.onError] Structure-based errorPayload result:', errorPayload)
      }

      console.log('[manage_business.onError] ========== FINAL ERROR PAYLOAD ==========')
      console.log('[manage_business.onError] Extracted error payload:', JSON.stringify(errorPayload, null, 2))
      console.log('[manage_business.onError] Error payload type:', typeof errorPayload)
      console.log('[manage_business.onError] Error payload keys:', errorPayload ? Object.keys(errorPayload) : 'null')

      // Helper function to extract error message for fallback
      const extractErrorMessage = (errorData) => {
        const payloads = [
          errorData && errorData.data,
          errorData && errorData.response && errorData.response.data,
          errorData && errorData.body,
          errorData
        ].filter(Boolean)

        for (const payload of payloads) {
          if (payload && payload.status && payload.status.messageDescription) {
            return payload.status.messageDescription
          }
          if (payload && payload.messageDescription) {
            return payload.messageDescription
          }
          if (payload && payload.message) {
            return payload.message
          }
          if (payload && payload.error) {
            return payload.error
          }
        }
        return null
      }

      // If no errorType found, check for legacy format with multi-platform sync error message
      if (!errorPayload || !errorPayload.errorType) {
        console.error('[manage_business.onError] Missing errorType in error response:', data)
        const errorMessage = extractErrorMessage(data) || 'An error occurred. Please try again.'

        // Fallback: Check if this is a multi-platform sync error in legacy format
        const isMultiPlatformSyncError =
          errorMessage &&
          typeof errorMessage === 'string' &&
          (errorMessage.includes('Failed to sync with third-party platforms') ||
            errorMessage.includes('One or more social platforms failed to sync'))

        if (isMultiPlatformSyncError && isSyncEnabled) {
          console.log('[manage_business.onError] Detected multi-platform sync error in legacy format, showing modal')

          // Extract connected platforms from socialConnections to populate error details
          const socialConnections = state.socialConnections || []
          const connectedPlatforms = []
          const platformsMap = {}

          // Build platform entries from connected social platforms
          socialConnections.forEach((connection) => {
            if (connection && connection.type) {
              const platformType = connection.type.toUpperCase()
              if (platformType === 'GOOGLE' || platformType === 'FACEBOOK') {
                connectedPlatforms.push(platformType)
                // Create platform entry with error message from the legacy response
                platformsMap[platformType] = {
                  platform: platformType,
                  status: 'FAILED',
                  errorMessage: errorMessage || i18n.t('settings.sync-failed-generic'),
                  errorDetails: {
                    summary: errorMessage || i18n.t('settings.one-or-more-platforms-failed'),
                    detailedMessage:
                      errorMessage ||
                      (platformType === 'GOOGLE'
                        ? i18n.t('settings.sync-failed-google')
                        : i18n.t('settings.sync-failed-facebook'))
                  },
                  canDisconnect: true
                }
              }
            }
          })

          // If no connections found, assume both platforms might be connected and show generic error for both
          // This handles cases where socialConnections might not be loaded yet
          if (connectedPlatforms.length === 0) {
            connectedPlatforms.push('GOOGLE', 'FACEBOOK')
            platformsMap.GOOGLE = {
              platform: 'GOOGLE',
              status: 'FAILED',
              errorMessage: errorMessage || i18n.t('settings.sync-failed-generic'),
              errorDetails: {
                summary: errorMessage || i18n.t('settings.one-or-more-platforms-failed'),
                detailedMessage: errorMessage || i18n.t('settings.sync-failed-google')
              },
              canDisconnect: true
            }
            platformsMap.FACEBOOK = {
              platform: 'FACEBOOK',
              status: 'FAILED',
              errorMessage: errorMessage || i18n.t('settings.sync-failed-generic'),
              errorDetails: {
                summary: errorMessage || i18n.t('settings.one-or-more-platforms-failed'),
                detailedMessage: errorMessage || i18n.t('settings.sync-failed-facebook')
              },
              canDisconnect: true
            }
          }

          // Show modal with platform information populated
          return {
            ...state,
            loading: false,
            apiError: true,
            multiPlatformErrorModal: {
              visible: true,
              summary: i18n.t('settings.one-or-more-platforms-failed'),
              platforms: platformsMap,
              failedPlatforms: connectedPlatforms,
              succeededPlatforms: [],
              partialSuccess: false,
              showDetails: false
            },
            gmbErrorModal: null,
            facebookErrorModal: null
          }
        }

        // Handle specific business logic errors
        let finalErrorMessage = errorMessage
        if (errorMessage && Array.isArray(errorMessage) && errorMessage.includes('Business hours overlapped')) {
          finalErrorMessage = 'Overlapping time slots found in business hours'
        } else if (
          errorMessage &&
          typeof errorMessage === 'string' &&
          errorMessage.includes(GOOGLE_CONNECTED_ALREADY_ERROR_MSG)
        ) {
          finalErrorMessage = GOOGLE_CONNECTED_ALREADY_ERROR_MSG
        }

        notification('error', capitalize(finalErrorMessage))
        return {
          ...state,
          loading: false,
          apiError: true,
          gmbErrorModal: null,
          multiPlatformErrorModal: null,
          facebookErrorModal: null
        }
      }

      // Route based on errorType
      console.log('[manage_business.onError] ========== ERROR ROUTING ==========')
      console.log('[manage_business.onError] Error payload with errorType:', errorPayload && errorPayload.errorType)
      console.log('[manage_business.onError] Full error payload:', JSON.stringify(errorPayload, null, 2))
      console.log('[manage_business.onError] Full error data structure:', JSON.stringify(data, null, 2))
      console.log('[manage_business.onError] Current state sync flag:', isSyncEnabled)

      // If errorPayload exists but errorType is missing, check if it's a MULTI_PLATFORM_SYNC_FAILED structure
      if (
        errorPayload &&
        !errorPayload.errorType &&
        (errorPayload.platforms || errorPayload.failedPlatforms || errorPayload.succeededPlatforms)
      ) {
        console.log('[manage_business.onError] ========== AUTO-SETTING ERROR TYPE ==========')
        console.log(
          '[manage_business.onError] Detected MULTI_PLATFORM_SYNC_FAILED structure without errorType, setting it'
        )
        console.log('[manage_business.onError] Structure check:', {
          hasPlatforms: !!errorPayload.platforms,
          hasFailedPlatforms: !!errorPayload.failedPlatforms,
          hasSucceededPlatforms: !!errorPayload.succeededPlatforms
        })
        errorPayload.errorType = 'MULTI_PLATFORM_SYNC_FAILED'
        console.log('[manage_business.onError] errorType set to:', errorPayload.errorType)
      }

      // Ensure we have an errorPayload before switching
      if (!errorPayload) {
        console.error('[manage_business.onError] ========== NO ERROR PAYLOAD FOUND ==========')
        console.error('[manage_business.onError] No errorPayload found, cannot route error')
        console.error('[manage_business.onError] All possible payloads were:', possiblePayloads)
        const errorMessage = extractErrorMessage(data) || 'An error occurred. Please try again.'
        console.error('[manage_business.onError] Extracted error message:', errorMessage)
        notification('error', errorMessage)
        return {
          ...state,
          loading: false,
          apiError: true
        }
      }

      console.log('[manage_business.onError] ========== SWITCHING ON ERROR TYPE ==========')
      console.log('[manage_business.onError] errorType to route:', errorPayload.errorType)
      switch (errorPayload.errorType) {
        case 'GOOGLE_CONFIGURATION_REQUIRED':
          console.log('[manage_business.onError] Routing to handleGoogleError')
          return this.handleGoogleError(state, errorPayload, isSyncEnabled)

        case 'FACEBOOK_CONFIGURATION_REQUIRED':
          console.log('[manage_business.onError] Routing to handleFacebookError')
          return this.handleFacebookError(state, errorPayload, isSyncEnabled)

        case 'MULTI_PLATFORM_SYNC_FAILED':
          console.log('[manage_business.onError] ========== MULTI_PLATFORM_SYNC_FAILED DETECTED ==========')
          console.log('[manage_business.onError] Routing to handleMultiPlatformError')
          console.log('[manage_business.onError] Error payload being passed:', JSON.stringify(errorPayload, null, 2))
          console.log('[manage_business.onError] Payload structure:', {
            hasSummary: !!errorPayload.summary,
            hasPlatforms: !!errorPayload.platforms,
            hasFailedPlatforms: !!errorPayload.failedPlatforms,
            hasSucceededPlatforms: !!errorPayload.succeededPlatforms,
            platformsKeys: errorPayload.platforms ? Object.keys(errorPayload.platforms) : [],
            failedPlatforms: errorPayload.failedPlatforms,
            succeededPlatforms: errorPayload.succeededPlatforms
          })
          try {
            console.log('[manage_business.onError] About to call handleMultiPlatformErrorHelper')
            const result = handleMultiPlatformErrorHelper(state, errorPayload)
            console.log('[manage_business.onError] ========== HANDLE MULTI PLATFORM ERROR RETURNED ==========')
            console.log('[manage_business.onError] handleMultiPlatformErrorHelper returned state:', result)
            console.log(
              '[manage_business.onError] Modal state in result:',
              JSON.stringify(result.multiPlatformErrorModal, null, 2)
            )
            console.log(
              '[manage_business.onError] Modal visible in result:',
              result.multiPlatformErrorModal && result.multiPlatformErrorModal.visible
            )
            console.log('[manage_business.onError] ========== RETURNING NEW STATE ==========')
            return result
          } catch (error) {
            console.error('[manage_business.onError] ERROR in handleMultiPlatformErrorHelper:', error)
            console.error('[manage_business.onError] Error stack:', error.stack)
            throw error
          }

        default:
          // Unknown errorType - log warning and show generic error
          console.warn('[manage_business.onError] Unknown errorType:', errorPayload.errorType, errorPayload)
          notification(
            'error',
            errorPayload.message || errorPayload.detailedMessage || 'An error occurred. Please try again.'
          )
          return {
            ...state,
            loading: false,
            apiError: true,
            gmbErrorModal: null,
            multiPlatformErrorModal: null,
            facebookErrorModal: null
          }
      }
    },
    handleGoogleError(state, payload, isSyncEnabled) {
      console.log('[manage_business.handleGoogleError] Google error detected', payload)

      if (!isSyncEnabled) {
        const fallbackMessage =
          payload.detailedMessage || payload.message || 'Action required in Google Business Profile'
        console.log('[manage_business.handleGoogleError] Sync flag disabled, using toaster fallback')
        notification('error', fallbackMessage)
        return {
          ...state,
          loading: false,
          apiError: true,
          gmbErrorModal: null
        }
      }

      const issues = Array.isArray(payload.issues) ? payload.issues : payload.issues ? [payload.issues] : []

      const normalizedIssues = issues.map((issue, index) => {
        if (issue && typeof issue === 'object') {
          return {
            reason: issue.reason || `GOOGLE_ISSUE_${index}`,
            summary: issue.summary || issue.message || issue.detailedMessage || issue.reason || 'Action required',
            why: issue.why || issue.reasonWhy || null,
            howToFix: issue.howToFix || issue.resolution || null,
            detailedMessage: issue.detailedMessage || issue.message || null,
            helpUrl: issue.helpUrl
          }
        }
        const value = typeof issue === 'string' ? issue : JSON.stringify(issue)
        return {
          reason: `GOOGLE_ISSUE_${index}`,
          summary: value,
          detailedMessage: value,
          helpUrl: payload.helpUrl
        }
      })

      notification('warning', payload.message || 'Action required in Google Business Profile')
      return {
        ...state,
        loading: false,
        apiError: true,
        gmbErrorModal: {
          visible: true,
          summary: payload.message || 'Action required in Google Business Profile',
          detailedMessage: payload.detailedMessage || payload.message,
          issues: normalizedIssues,
          helpUrl: payload.helpUrl || 'https://business.google.com'
        },
        multiPlatformErrorModal: null,
        facebookErrorModal: null
      }
    },
    handleFacebookError(state, payload, isSyncEnabled) {
      console.log('[manage_business.handleFacebookError] Facebook error detected', payload)

      if (!isSyncEnabled) {
        const fallbackMessage = payload.detailedMessage || payload.message || 'Action required in Facebook Page'
        console.log('[manage_business.handleFacebookError] Sync flag disabled, using toaster fallback')
        notification('error', fallbackMessage)
        return {
          ...state,
          loading: false,
          apiError: true,
          facebookErrorModal: null
        }
      }

      notification('warning', payload.message || 'Action required in Facebook Page')
      return {
        ...state,
        loading: false,
        apiError: true,
        facebookErrorModal: {
          visible: true,
          summary: payload.message || 'Action required in Facebook Page',
          detailedMessage: payload.detailedMessage || payload.message,
          helpUrl: payload.helpUrl || 'https://www.facebook.com/business/help'
        },
        gmbErrorModal: null,
        multiPlatformErrorModal: null
      }
    },
    handleMultiPlatformError(state, payload) {
      // Call the helper function to avoid 'this' context issues
      return handleMultiPlatformErrorHelper(state, payload)
    },
    setGmbErrorModal(state, data) {
      return {
        ...state,
        gmbErrorModal: data
      }
    },
    setFacebookErrorModal(state, data) {
      return {
        ...state,
        facebookErrorModal: data
      }
    },
    setMultiPlatformErrorModal(state, data) {
      return {
        ...state,
        multiPlatformErrorModal: data
      }
    },
    setSyncWithThirdPartiesSynchronously(state, value) {
      return {
        ...state,
        syncWithThirdPartiesSynchronously: Boolean(value)
      }
    },
    onbusinessSuccess(state, data, msg) {
      if (msg) {
        notification('success', msg)
      }

      let businessSettings = data && data.location ? data.location : data && Object.keys(data).length ? data : null
      console.log('get businessSettings--------------------------------')
      if (localStorage.getItem('saveLocal') === 'true' && localStorage.getItem('businessSettings')) {
        businessSettings = JSON.parse(localStorage.getItem('businessSettings'))
        console.log('businessSettings fetch from local')
      }
      return {
        ...state,
        businessSettings,
        loading: false,
        apiError: false
      }
    },
    onUpdateBusinessSettingsLocalSuccess(state, data) {
      const businessSettings = { ...state.businessSettings, ...data }

      localStorage.setItem('businessSettings', JSON.stringify(businessSettings))
      return {
        ...state,
        loading: false,
        businessSettings
      }
    },
    getStaffUsersSuccess(state, data) {
      return {
        ...state,
        loading: false,
        staffs: data
      }
    },
    getCustomFieldsSuccess(state, data) {
      return {
        ...state,
        loading: false,
        fields: data.customFields
      }
    },
    getCustomFieldSuccess(state, data) {
      return {
        ...state,
        loading: false,
        editField: data
      }
    },
    onactivities(state, data) {
      return {
        ...state,
        loading: false,
        activityList: data
      }
    },
    onGetPrimaryCategoriesSuccess(state, data) {
      return {
        ...state,
        loading: false,
        primaryCategories: data
      }
    },
    onGetOnboardingCategoriesSuccess(state, data) {
      return {
        ...state,
        loading: false,
        boardingCategories: data
      }
    },
    onGetOnboardingCategoriesOtherSuccess(state, data) {
      return {
        ...state,
        loading: false,
        boardingCategoriesOther: data
      }
    },
    onGetSeconderyCategoriesSuccess(state, data) {
      return {
        ...state,
        loading: false,
        secondaryCategories: data
      }
    },
    onUpdateMediaPhotoNameSuccess(state) {
      notification('success', 'Photo description successfully updated')
      return {
        ...state,
        loading: false
      }
    },
    onUpdateStaffStatusSuccess(state, data, status) {
      if (status) {
        let msg = ''
        if (status === 'active') {
          msg = 'User enabled successfully'
        } else if (status === 'inactive') {
          msg = 'User disabled successfully'
        } else if (status === 'deleted') {
          msg = 'User deleted successfully'
        }
        if (msg) {
          notification('success', msg)
        }
      }
      return {
        ...state,
        loading: false
      }
    },
    onSyncSuccess(state, data) {
      notification('success', data.data ? data.data : data.message)
      return {
        ...state,
        loading: false
      }
    },
    onSocialConnectionsSuccess(state, data) {
      return {
        ...state,
        loading: false,
        socialConnections: data.data
      }
    },
    onfetchSmsThemeSuccess(state, data) {
      return {
        ...state,
        loading: false,
        smsTemplates: data
      }
    },
    onDeleteMediaPhotoSuccess(state) {
      return {
        ...state,
        loading: false
      }
    },
    onDeleteMediaVideoSuccess(state, data) {
      notification('success', 'Video deleted successfully')
      return {
        ...state,
        loading: false,
        data: data
      }
    },

    onResetUserAccountSuccess(state) {
      notification('success', 'A reset password link has been sent to this user')
      return {
        ...state,
        loading: false
      }
    },
    onQuickLoginSuccess(state) {
      notification('success', 'A quick login link has been sent to this user')
      return {
        ...state,
        loading: false
      }
    },
    onCreateStaffSuccess(state, data) {
      notification('success', 'The staff has been created successfully')
      let newStaffId = ''
      if (data && data.data) {
        newStaffId = data.data
      }
      return {
        ...state,
        loading: false,
        apiError: false,
        newStaffId: newStaffId
      }
    },
    onDeleteCustomFieldSuccess() {
      notification('success', 'The custom field has been deleted successfully')
    },
    onEditCustomFieldSuccess(state, data) {
      notification('success', 'The custom field has been updated successfully')
      let editField = ''
      if (data) {
        editField = data
      }
      return {
        ...state,
        loading: false,
        apiError: false,
        editField: editField
      }
    },
    onCreateCustomFieldSuccess(state, data) {
      notification('success', 'The custom field has been created successfully')
      let newFieldId = ''
      if (data) {
        newFieldId = data
      }
      return {
        ...state,
        loading: false,
        apiError: false,
        newFieldId: newFieldId
      }
    },
    onUserRolePermissions(state) {
      notification('success', 'The staff permission updated successfully')
      return {
        ...state,
        loading: false
      }
    },
    onGetGmbDataOfUser(state, data) {
      return {
        ...state,
        loading: false,
        gmbDataOfUser: data
      }
    },
    onBeforeGetGmbAccounts(state, data) {
      return {
        ...state,
        loading: true,
        gmbData: []
      }
    },
    onGetGmbAccountsSuccess(state, data) {
      return {
        ...state,
        loading: false,
        gmbData: data
      }
    },
    ontriggerContactSalesSuccess(state, data) {
      return {
        ...state,
        contactSalesStatus: data
      }
    },
    ontriggerContactSalesError(state, data) {
      return {
        ...state,
        contactSalesStatus: data
      }
    },
    setFacebookData(state, data) {
      return {
        ...state,
        loading: false,
        facebookData: data
      }
    },
    onSelectedSocialDetails(state, data) {
      return {
        ...state,
        loading: false,
        selectedSocialDetails: data
      }
    },
    onSetSelectedFacebookPage(state, data) {
      return {
        ...state,
        loading: false,
        selectedFacebookPage: data
      }
    },
    onGetGmbLocationsSuccess(state, data) {
      return {
        ...state,
        loading: false,
        gmbLocations: data && data.data
      }
    },
    onGmbActive(state, data) {
      let response = null
      if (data.status.statusCode === 1) {
        response = true
      } else if (data.status.statusCode === 0) {
        response = false
      }
      return {
        ...state,
        loading: false,
        isGmbActive: response
      }
    },
    onGbmEnabled(state, data) {
      let response = null
      if (data.status.statusCode === 1) {
        response = true
      } else if (data.status.statusCode === 0) {
        response = false
      }
      return {
        ...state,
        loading: false,
        isGbmEnabled: response
      }
    },
    onEmbedChatScriptSuccess(state, data) {
      return {
        ...state,
        loading: false,
        embedcode: data.data
      }
    },
    onEmbedBookingScriptSuccess(state, data) {
      return {
        ...state,
        loading: false,
        bookingEmbedCode: data.data
      }
    },
    ongetMenuLocationsSuccess(state, data) {
      return {
        ...state,
        loading: false,
        locationMenus: data.data
      }
    },
    onGetsettings(state, data) {
      return {
        ...state,
        loading: false,
        goalsList: data
      }
    },
    onaddSectionitemSuccess(state, data) {
      notification('success', 'Menu section added')
      return {
        ...state,
        loading: false,
        newAddedSection: data.data
      }
    },
    onfetchUserRolePermissionsSuccess(state, data) {
      return {
        ...state,
        loading: false,
        teamUserPermissions: data && data.data
      }
    },
    ongetUserSuccess(state, data) {
      return {
        ...state,
        loading: false,
        teamUserData: data
      }
    },
    onGetCalendarDataSuccess(state, data) {
      return {
        ...state,
        loading: false,
        teamUserCalendar: data && data.data
      }
    },
    onupdateStaffUserSuccess(state, data, msg) {
      if (msg) {
        notification('success', msg)
      }
      return {
        ...state,
        loading: false
      }
    },
    onSendWelcomeMessageSuccess(state, data, msg) {
      if (msg) {
        notification('success', msg)
      }
      return {
        ...state,
        loading: false
      }
    },
    ongetActivityListSuccess(state, data) {
      let delCount = 0
      let activitiesTotl = 0
      for (let i = data.docs.length - 1; i >= 0; i--) {
        if (data.docs[i].meta_data && data.docs[i].meta_data.succesful === 0) {
          data.docs[i].delstatus = 'deleted'
          delCount++
        }
      }
      for (let i = data.docs.length - 1; i >= 0; i--) {
        if (!data.docs[i].status || (data.docs[i].status && data.docs[i].status !== 'canceled')) {
          if (data.docs[i].display_message) activitiesTotl++
        }
      }
      let array = data.docs && data.docs.length ? data.docs : []
      if (state.teamActivityList && state.teamActivityList.docs && state.teamActivityList.docs.length) {
        array = [...state.teamActivityList.docs, ...array]
      }
      data.docs = array
      return {
        ...state,
        loading: false,
        teamActivityList: data,
        activitiesTotl: activitiesTotl,
        delCount: delCount
      }
    },
    unLinkCalendarSuccess(state, data, msg) {
      if (msg) {
        notification('success', msg)
      }
      return {
        ...state,
        loading: false
      }
    },
    onGetMenuOptionsSuccess(state, data) {
      return {
        ...state,
        loading: false,
        menuLabel: data && data.data
      }
    },
    clearTeamActivityData(state) {
      return {
        ...state,
        loading: false,
        teamActivityList: null,
        activitiesTotl: null,
        delCount: null
      }
    }
  },

  effects: {
    async fetchSyncWithThirdPartiesFlag(payload, rootState) {
      try {
        const meData = (payload && payload._id && payload) || (rootState && rootState.auth && rootState.auth.me)
        const resolvedMe = meData || (await me())
        console.log('[manage_business.fetchSyncWithThirdPartiesFlag] resolvedMe id', resolvedMe && resolvedMe._id)
        if (!resolvedMe) {
          console.warn('[manage_business.fetchSyncWithThirdPartiesFlag] No user context available')
          return false
        }
        const flagValue = await service.getConfigCatValue('syncWithThirdPartiesSynchronously', resolvedMe)
        console.log('[manage_business.fetchSyncWithThirdPartiesFlag] flag value =', flagValue)
        this.setSyncWithThirdPartiesSynchronously(Boolean(flagValue))
        return flagValue
      } catch (error) {
        console.error('[manage_business.fetchSyncWithThirdPartiesFlag] Failed to load flag', error)
        return false
      }
    },
    async fetchBusinessSettings(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.fetchBusinessSettings(payload)
        this.onbusinessSuccess(res.data)
        return res
      } catch (e) {
        // this.onError(e);
        console.log(e)
      }
    },
    async getServiceAreas(payload) {
      this.onRequest()
      try {
        const res = await service.getServiceAreas(payload)
        this.onbusinessSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    // Read-only preview: returns { business_area: [...] } without touching stored settings state.
    async resolveServiceArea(payload) {
      try {
        return await service.resolveServiceArea(payload)
      } catch (e) {
        console.log(e)
        // Surface the server-side reason (e.g. an invalid ZIP) so the caller can show it
        // instead of a generic "lookup failed".
        return { error: (e && e.data && e.data.error) || null }
      }
    },
    async updateBusinessSettings(payload) {
      const throwBackErr = payload.throwBackErr
      delete payload.throwBackErr
      try {
        const type = payload.type
        const saveLocal = localStorage.getItem('saveLocal') === 'true'
        let res
        console.log('-------------- here saveLocal-----------------', saveLocal, localStorage.getItem('saveLocal'))
        if (saveLocal) {
          this.onUpdateBusinessSettingsLocalSuccess(payload)
        } else {
          console.log('[manage_business.updateBusinessSettings] ========== START API CALL ==========')
          console.log('[manage_business.updateBusinessSettings] Payload:', JSON.stringify(payload, null, 2))
          res = await service.updateBusinessSettings(payload)
          console.log('[manage_business.updateBusinessSettings] ========== API RESPONSE RECEIVED ==========')
          console.log('[manage_business.updateBusinessSettings] Response type:', typeof res)
          console.log('[manage_business.updateBusinessSettings] Response:', JSON.stringify(res, null, 2))
          console.log('[manage_business.updateBusinessSettings] Response keys:', res ? Object.keys(res) : 'null')

          const looksLikeErrorPayload = Boolean(
            res &&
              typeof res === 'object' &&
              (res.errorType ||
                (res.success === false && (res.summary || res.message || res.status)) ||
                (res.status && res.status.statusCode === 0))
          )

          console.log('[manage_business.updateBusinessSettings] looksLikeErrorPayload check:', {
            hasErrorType: !!(res && res.errorType),
            hasSuccessFalse: !!(res && res.success === false),
            hasSummary: !!(res && res.summary),
            hasMessage: !!(res && res.message),
            hasStatus: !!(res && res.status),
            statusCode: res && res.status ? res.status.statusCode : 'N/A',
            result: looksLikeErrorPayload
          })

          if (looksLikeErrorPayload) {
            console.warn('[manage_business.updateBusinessSettings] ========== ERROR-LIKE PAYLOAD DETECTED ==========')
            console.warn('[manage_business.updateBusinessSettings] Error payload:', JSON.stringify(res, null, 2))
            console.warn(
              '[manage_business.updateBusinessSettings] Forwarding to onError handler with shape: { data: res }'
            )
            // Mimic axios error shape so onError can reuse existing parsing logic
            this.onError({ data: res })
            console.log('[manage_business.updateBusinessSettings] ========== onError CALLED ==========')
          } else {
            this.onbusinessSuccess(res, type && res && !res.errorMsgCreateJiraTicketUberall ? getMessages(type) : null)
            if (res && res.errorMsgCreateJiraTicketUberall) {
              notification('error', res.errorMsgCreateJiraTicketUberall)
            }
          }
        }
        return res
      } catch (e) {
        this.onError(e)
        if (throwBackErr) {
          throw e
        }
      }
    },
    async getStaffUsers(data) {
      this.onRequest()
      try {
        const res = await service.getStaffUsers(data)
        this.getStaffUsersSuccess(res.docs)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getCustomFields() {
      this.onRequest()
      try {
        const res = await service.getCustomFields()
        this.getCustomFieldsSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getCustomField(fieldId) {
      this.onRequest()
      try {
        const res = await service.getCustomField(fieldId)
        this.getCustomFieldSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async deleteCustomField(fieldId) {
      try {
        const res = await service.deleteCustomField(fieldId)
        this.onDeleteCustomFieldSuccess(res)
        return res
      } catch (e) {
        notification('error', capitalize(e && e.data && e.data.error))
        this.onError(e)
      }
    },
    async activities(data) {
      this.onRequest()
      try {
        const res = await service.activities(data)
        await this.onactivities(res.docs)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getPrimaryCategories(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getCategories(payload)
        this.onGetPrimaryCategoriesSuccess(res.data)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getSecondaryCategories(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getCategories(payload)
        this.onGetSeconderyCategoriesSuccess(res.data)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getOnboardingCategories(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getOnboardingCategories(payload)
        this.onGetOnboardingCategoriesSuccess(res.data)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getOnboardingCategoriesOther(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getOnboardingCategories(payload)
        this.onGetOnboardingCategoriesOtherSuccess(res.data)
        return res
      } catch (e) {
        this.onError(e)
      }
    },

    async updateMediaPhotoName(payload) {
      this.onRequest()
      try {
        const res = await service.updateMediaPhotoName(payload)
        this.onUpdateMediaPhotoNameSuccess(res.data)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async triggerContactSales(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.triggerContactSales(payload)
        this.ontriggerContactSalesSuccess(res, payload)
        return res
      } catch (e) {
        console.log(e)
        this.ontriggerContactSalesError(e)
      }
    },
    async facebookSync(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.facebookSync(payload)
        this.onSyncSuccess(res)
        return res
      } catch (e) {
        if (e && e.data && e.data.error) {
          notification('error', capitalize(e.data.error))
        } else {
          this.onError(e)
        }
      }
    },
    async facebookUnSync(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.facebookUnSync(payload)
        this.onSyncSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getSocialConnections(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getSocialConnections(payload)
        this.onSocialConnectionsSuccess(res)
      } catch (e) {
        this.onError(e)
      }
    },
    async updateStaffStatus(payload) {
      this.onRequest()
      try {
        const res = await service.updateStaffStatus(payload)
        this.onUpdateStaffStatusSuccess(res.data, payload.status)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async fetchSmsTheme(payload) {
      this.onRequest()
      try {
        const res = await service.fetchSmsTheme(payload)
        this.onfetchSmsThemeSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },

    async getsettings(payload) {
      this.onRequest()
      try {
        const res = await service.getsettings(payload)
        await this.onGetsettings(res.settings)
        return res.settings
      } catch (e) {
        // this.onError(e);
        console.log(e)
      }
    },
    async deleteMediaPhoto(payload) {
      this.onRequest()
      try {
        const res = await service.deleteMediaPhoto(payload)
        if (res && res.errorMsgCreateJiraTicketUberall) {
          notification('error', res.errorMsgCreateJiraTicketUberall)
          this.onDeleteMediaPhotoSuccess(res.data)
        } else {
          notification('success', 'Successfully deleted photo')
          this.onDeleteMediaPhotoSuccess(res.data)
        }
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async deleteMediaVideo(payload) {
      this.onRequest()
      try {
        const res = await service.deleteMediaVideo(payload)
        this.onDeleteMediaVideoSuccess(res.data)
        return res
      } catch (e) {
        this.onError(e)
      }
    },

    async resetUserAccount(payload) {
      try {
        const res = await service.resetUserAccount(payload)
        this.onResetUserAccountSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async quickLogin(payload) {
      try {
        const res = await service.quickLogin(payload)
        this.onQuickLoginSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async createStaff(payload, state) {
      try {
        const res = await service.createStaff(payload)
        this.onCreateStaffSuccess(res)
        return res
      } catch (e) {
        notification('error', capitalize(e && e.data && e.data.error))
        this.onError(e)
      }
    },

    async createCustomField(payload, state) {
      try {
        const res = await service.createCustomField(payload)
        this.onCreateCustomFieldSuccess(res)
        return res
      } catch (e) {
        notification('error', capitalize(e && e.data && e.data.error))
        this.onError(e)
      }
    },

    async editCustomField(payload, state) {
      try {
        const res = await service.editCustomField(payload)
        this.onEditCustomFieldSuccess(res)
        return res
      } catch (e) {
        notification('error', capitalize(e && e.data && e.data.error))
        this.onError(e)
      }
    },

    async fetchUserRolePermissions(payload) {
      this.onRequest()
      try {
        const res = await service.fetchUserRolePermissions(payload)
        this.onfetchUserRolePermissionsSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getGmbAccounts(payload, rootState) {
      try {
        const res = await service.getGmbAccounts(payload)
        // sample res from gmb
        // const res = {
        //   'accounts': [
        //     {
        //       'name': 'accounts/102737417257825406976',
        //       'accountName': 'Rohan Sudhir Bhore',
        //       'type': 'PERSONAL',
        //       'verificationState': 'UNVERIFIED',
        //       'vettedState': 'NOT_VETTED'
        //     }
        //   ]
        // }
        this.onGetGmbAccountsSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getGMBDetailsFromGoogle(payload) {
      try {
        this.onBeforeGetGmbAccounts()
        const res = await service.getGMBDetailsFromGoogle(payload)
        this.onGetGmbAccountsSuccess(res.data)
        return res.data
      } catch (e) {
        notification('error', capitalize('GMB Account is not available Please Proceed without GMB'))
      }
    },
    setSelectedSocialDetails(payload) {
      try {
        this.onSelectedSocialDetails(payload)
      } catch (e) {
        this.error(e)
      }
    },
    setSelectedFacebookPage(payload) {
      try {
        this.onSetSelectedFacebookPage(payload)
      } catch (e) {
        this.error(e)
      }
    },
    async connectWithFacebook(payload) {
      try {
        const res = await service.connectFacebook(payload)
        this.setFacebookData(res.data)
        return res.data
      } catch (e) {
        notification('error', capitalize('Facebook Page is not available Please Proceed without Facebook Sync'))
        this.onError(e)
      }
    },
    async userRolePermissions(payload) {
      try {
        const res = await service.userRolePermissions(payload)
        this.onUserRolePermissions(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getGmbLocations(payload, rootState) {
      console.log('[model.getGmbLocations] : Called with payload : ', JSON.stringify(payload, null, 2))
      try {
        //  const res1 = { 'message': 'Success', 'data': [{ 'name': 'locations/3692385607928883086', 'languageCode': 'en', 'title': 'Valley Christian Schools', 'phoneNumbers': { 'primaryPhone': '(408) 513-2400' }, 'categories': { 'primaryCategory': { 'name': 'categories/gcid:awning_supplier', 'displayName': 'Awning supplier', 'serviceTypes': [{ 'serviceTypeId': 'job_type_id:delivery', 'displayName': 'Delivery' }], 'moreHoursTypes': [{ 'hoursTypeId': 'ACCESS', 'displayName': 'Access', 'localizedDisplayName': 'Access' }, { 'hoursTypeId': 'BREAKFAST', 'displayName': 'Breakfast', 'localizedDisplayName': 'Breakfast' }, { 'hoursTypeId': 'BRUNCH', 'displayName': 'Brunch', 'localizedDisplayName': 'Brunch' }, { 'hoursTypeId': 'DELIVERY', 'displayName': 'Delivery', 'localizedDisplayName': 'Delivery' }, { 'hoursTypeId': 'DINNER', 'displayName': 'Dinner', 'localizedDisplayName': 'Dinner' }, { 'hoursTypeId': 'DRIVE_THROUGH', 'displayName': 'Drive through', 'localizedDisplayName': 'Drive through' }, { 'hoursTypeId': 'HAPPY_HOUR', 'displayName': 'Happy hours', 'localizedDisplayName': 'Happy hours' }, { 'hoursTypeId': 'KITCHEN', 'displayName': 'Kitchen', 'localizedDisplayName': 'Kitchen' }, { 'hoursTypeId': 'LUNCH', 'displayName': 'Lunch', 'localizedDisplayName': 'Lunch' }, { 'hoursTypeId': 'ONLINE_SERVICE_HOURS', 'displayName': 'Online service hours', 'localizedDisplayName': 'Online service hours' }, { 'hoursTypeId': 'PICKUP', 'displayName': 'Pickup', 'localizedDisplayName': 'Pickup' }, { 'hoursTypeId': 'TAKEOUT', 'displayName': 'Takeout', 'localizedDisplayName': 'Takeout' }, { 'hoursTypeId': 'SENIOR_HOURS', 'displayName': 'Senior hours', 'localizedDisplayName': 'Senior hours' }] } }, 'storefrontAddress': { 'regionCode': 'US', 'languageCode': 'en', 'postalCode': '95111', 'administrativeArea': 'CA', 'locality': 'San Jose', 'addressLines': ['100 Skyway Dr #130, San Jose, CA, 95111'] }, 'websiteUri': 'http://www.vcs.net/', 'regularHours': { 'periods': [{ 'openDay': 'MONDAY', 'openTime': { 'hours': 9 }, 'closeDay': 'MONDAY', 'closeTime': { 'hours': 17 } }, { 'openDay': 'TUESDAY', 'openTime': { 'hours': 9 }, 'closeDay': 'TUESDAY', 'closeTime': { 'hours': 17 } }, { 'openDay': 'WEDNESDAY', 'openTime': { 'hours': 9 }, 'closeDay': 'WEDNESDAY', 'closeTime': { 'hours': 17 } }, { 'openDay': 'THURSDAY', 'openTime': { 'hours': 9 }, 'closeDay': 'THURSDAY', 'closeTime': { 'hours': 17 } }, { 'openDay': 'FRIDAY', 'openTime': { 'hours': 9 }, 'closeDay': 'FRIDAY', 'closeTime': { 'hours': 17 } }] }, 'openInfo': { 'status': 'OPEN', 'canReopen': true }, 'metadata': { 'canDelete': true, 'canModifyServiceList': true, 'placeId': 'ChIJ17MWrU5J2YARjvpA19_O0CI', 'mapsUri': 'https://maps.google.com/maps?cid=2508732453229755022', 'newReviewUri': 'https://search.google.com/local/writereview?placeid=ChIJ17MWrU5J2YARjvpA19_O0CI', 'canHaveBusinessCalls': true }, 'profile': { 'description': 'Some business description' } }] }
        const res = await service.getGmbLocations(payload)
        console.log('[model.getGmbLocations] : Service call completed successfully')
        console.log('[model.getGmbLocations] : Response : ', JSON.stringify(res, null, 2))
        this.onGetGmbLocationsSuccess(res)
        return res
      } catch (e) {
        console.error('[model.getGmbLocations] : Error occurred : ', e)
        this.onError(e)
      }
    },
    async gmbImport(payload, rootState) {
      try {
        const res = await service.gmbImport(payload)
        this.onSuccess(res, 'Your Google account has been linked successfully')

        const meRes = await me()
        const configCat = await service.getConfigCatValue('gbmEnabled', meRes)
        if (configCat) {
          const gbmPayload = {}
          gbmPayload.accessToken = payload.access_token
          gbmPayload.refreshToken = payload.refresh_token
          gbmPayload.locationName = payload.location_name
          const responseGbm = await service.createGbmAgent(gbmPayload)
          await service.gbmEnabled()
          this.onSuccess(responseGbm, 'Business Message will be activated in couple of hours.')
        }

        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async gmbSync(payload, rootState) {
      try {
        const res = await service.gmbSync(payload)
        this.onSuccess(res, 'Your Google account has been synced successfully')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async gmbUnSync(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.gmbUnSync(payload)
        this.onSuccess(res, 'Google account unsynced successfully')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async gmbActive(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.gmbActive(payload)
        this.onGmbActive(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async gbmEnabled(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.gbmEnabled(payload)
        this.onGbmEnabled(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async createGbmAgent(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.createGbmAgent(payload)
        this.onSuccess(res, 'Google Business Message will be activated in couple of hours.')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async updateGbmAgent(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.updateGbmAgent(payload)
        this.onSuccess(res, 'GBM agent updated')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getGmbDataOfUser(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getGmbDataOfUser(payload)
        this.onGetGmbDataOfUser(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getEmbedChatScript(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getEmbedChatScript(payload)
        this.onEmbedChatScriptSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getEmbedBookingScript(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getEmbedBookingScript(payload)
        this.onEmbedBookingScriptSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async sortPhotos(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.sortPhotos(payload)
        this.onbusinessSuccess(res, 'Photo reordered successfully')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async uploadBusinessPhoto(payload, rootState, hideToaster) {
      this.onRequest()
      try {
        const res = await service.uploadBusinessPhoto(payload)
        this.onbusinessSuccess(
          res,
          !hideToaster && res && !res.errorMsgCreateJiraTicketUberall ? 'Photo uploaded successfully' : ''
        )
        if (res && res.errorMsgCreateJiraTicketUberall) {
          notification('error', res.errorMsgCreateJiraTicketUberall)
        }
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getMenuLocations(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getMenuLocations(payload)
        this.ongetMenuLocationsSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async addNewLocations(payload) {
      this.onRequest()
      try {
        const res = await service.upadteBusinessName(payload)
        const meRes = await me()
        this.onbusinessSuccess(res, 'New location added successfully')
        await new Promise((resolve) => setTimeout(resolve, 400))
        const { active_accounts, total_accounts } = meRes
        const { uuid } = setActiveTotalAccounts(active_accounts, total_accounts)
        return uuid
      } catch (e) {
        this.onError(e)
      }
    },
    async addNewGMBLocations(payload) {
      this.onRequest()
      try {
        const res = await service.createGMBLocation(payload)
        if (res) {
          const meRes = await me()
          this.onbusinessSuccess(res, 'New GMB location added successfully')
          await new Promise((resolve) => setTimeout(resolve, 400))
          const { active_accounts, total_accounts } = meRes
          const { uuid } = setActiveTotalAccounts(active_accounts, total_accounts)
          return uuid
        }
      } catch (e) {
        this.onError(e)
      }
    },
    async getUser(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getUser(payload)
        this.ongetUserSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getCalendarData(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getCalendarData(payload)
        this.onGetCalendarDataSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async updateStaffUser(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.updateStaffUser(payload)
        this.onupdateStaffUserSuccess(res, 'User has been updated successfully')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async sendWelcomeMessage(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.sendWelcomeMessage(payload)
        this.onSendWelcomeMessageSuccess(res, 'Welcome mail sent successfully')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getTeamActivityList(payload, rootState) {
      this.onRequest()
      try {
        const res = await service.getActivityList(payload)
        this.ongetActivityListSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async unLinkCalendar(payload, data) {
      this.onRequest()
      try {
        const res = await service.unLinkCalendar(payload)
        this.unLinkCalendarSuccess(res, 'You have successfully unsynced calendar')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async addSectionitem(payload, data) {
      this.onRequest()
      try {
        const res = await service.addSectionitem(payload)
        this.onaddSectionitemSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async updateSectionitem(payload, data) {
      this.onRequest()
      try {
        const res = await service.updateSectionitem(payload)
        this.onSuccess(res, 'Menu Section updated')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async getMenuOptions(payload) {
      this.onRequest()
      try {
        const res = await service.getMenuOptions(payload)
        this.onGetMenuOptionsSuccess(res)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async createMenuItem(payload) {
      this.onRequest()
      try {
        const res = await service.createMenuItem(payload)
        this.onSuccess(res, 'Menu Item added')

        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async updateMenuItem(payload) {
      this.onRequest()
      try {
        const res = await service.updateMenuItem(payload)
        this.onSuccess(res, 'Menu Item updated')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async deleteMenuItem(payload) {
      this.onRequest()
      try {
        const res = await service.deleteMenuItem(payload)
        this.onSuccess(res, 'Menu Item Deleted')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async deleteMenuSection(payload) {
      this.onRequest()
      try {
        const res = await service.deleteMenuSection(payload)
        this.onSuccess(res, 'Menu Section deleted')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async createMenu(payload) {
      this.onRequest()
      try {
        const res = await service.createMenu(payload)
        this.onSuccess(res, 'Menu Added')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async editMenu(payload) {
      this.onRequest()
      try {
        const res = await service.editMenu(payload)
        this.onSuccess(res, 'Menu Updated')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async deleteMenu(payload) {
      this.onRequest()
      try {
        const res = await service.deleteMenu(payload)
        this.onSuccess(res, 'Menu deleted')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async reorderMenuItem(payload) {
      this.onRequest()
      try {
        const res = await service.reorderMenuItem(payload)
        this.onSuccess(res, 'Updated menu order')
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async flushTeamActivityData() {
      try {
        this.clearTeamActivityData()
        return true
      } catch (e) {
        console.log(e)
      }
    },
    async syncGMBPhoto(payload) {
      try {
        const res = await service.syncGMBPhoto(payload)
        this.onSuccess(res, 'Photo will be synced shortly')
        return res
      } catch (e) {
        // this.onError(e);
      }
    },
    async uberAllCategory(payload, rootState, module) {
      try {
        const res = await service.uberAllCategory(payload, module)
        return res
      } catch (e) {
        console.log(e)
      }
    },
    async sendForwardPhoneOtp(payload) {
      try {
        const { locationId, ...body } = payload
        const res = await service.sendForwardPhoneOtp(locationId, body)
        return res
      } catch (e) {
        throw e
      }
    },
    async sendForwardPhoneOtpByCall(payload) {
      try {
        const { locationId, ...body } = payload
        const res = await service.sendForwardPhoneOtpByCall(locationId, body)
        return res
      } catch (e) {
        throw e
      }
    },
    async verifyForwardPhoneOtp(payload) {
      try {
        const { locationId, ...body } = payload
        const res = await service.verifyForwardPhoneOtp(locationId, body)
        if (res && res.success && res.callForwardPhone) {
          this.setCallForwardPhone(res.callForwardPhone)
        }
        return res
      } catch (e) {
        throw e
      }
    },
    async deleteForwardPhone(payload) {
      try {
        const { locationId } = payload
        const res = await service.deleteForwardPhone(locationId)
        if (res && res.success) {
          this.setCallForwardPhone(null)
        }
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async updateBusinessArea(payload) {
      try {
        const res = await service.updateBusinessArea(payload)
        return res
      } catch (e) {
        this.onError(e)
      }
    },
    async disconnectPlatformAndContinue(payload, rootState) {
      console.log('[disconnectPlatformAndContinue] ========== START DISCONNECT PLATFORM ==========')
      console.log('[disconnectPlatformAndContinue] Payload received:', JSON.stringify(payload, null, 2))
      console.log('[disconnectPlatformAndContinue] RootState available:', !!rootState)
      console.log(
        '[disconnectPlatformAndContinue] RootState manage_business available:',
        !!(rootState && rootState.manage_business)
      )

      const { platform } = payload
      console.log('[disconnectPlatformAndContinue] Platform to disconnect:', platform)

      try {
        // Get locationId from businessSettings in manage_business state
        console.log('[disconnectPlatformAndContinue] ========== STEP 1: GET LOCATION ID ==========')
        const locationId =
          rootState &&
          rootState.manage_business &&
          rootState.manage_business.businessSettings &&
          rootState.manage_business.businessSettings._id

        console.log('[disconnectPlatformAndContinue] Location ID extracted:', locationId)
        console.log(
          '[disconnectPlatformAndContinue] Business settings available:',
          !!(rootState && rootState.manage_business && rootState.manage_business.businessSettings)
        )
        console.log(
          '[disconnectPlatformAndContinue] Business settings _id:',
          rootState &&
            rootState.manage_business &&
            rootState.manage_business.businessSettings &&
            rootState.manage_business.businessSettings._id
        )

        if (!locationId) {
          console.error('[disconnectPlatformAndContinue] ERROR: Location ID not found')
          console.error('[disconnectPlatformAndContinue] RootState structure:', {
            hasRootState: !!rootState,
            hasManageBusiness: !!(rootState && rootState.manage_business),
            hasBusinessSettings: !!(
              rootState &&
              rootState.manage_business &&
              rootState.manage_business.businessSettings
            ),
            businessSettingsKeys:
              rootState && rootState.manage_business && rootState.manage_business.businessSettings
                ? Object.keys(rootState.manage_business.businessSettings)
                : []
          })
          throw new Error('Location ID not found')
        }

        // Call disconnect API
        console.log('[disconnectPlatformAndContinue] ========== STEP 2: CALL DISCONNECT API ==========')
        console.log('[disconnectPlatformAndContinue] API payload:', JSON.stringify({ locationId, platform }, null, 2))

        const response = await service.disconnectPlatformAndSave({
          locationId,
          platform
        })

        console.log('[disconnectPlatformAndContinue] API response received:', JSON.stringify(response, null, 2))
        console.log('[disconnectPlatformAndContinue] Response success:', !!(response && response.success))
        console.log('[disconnectPlatformAndContinue] Response error:', response && response.error)

        if (response && response.success) {
          const platformName = this.getPlatformName(platform)
          console.log('[disconnectPlatformAndContinue] Platform name for notification:', platformName)
          console.log(
            '[disconnectPlatformAndContinue] Success message:',
            response.message || `${platformName} disconnected successfully. Your changes have been saved.`
          )

          notification(
            'success',
            response.message || `${platformName} disconnected successfully. Your changes have been saved.`
          )
        } else {
          console.error('[disconnectPlatformAndContinue] ERROR: API response indicates failure')
          console.error('[disconnectPlatformAndContinue] Response:', response)
          throw new Error((response && response.error) || 'Failed to disconnect platform')
        }

        // Close modal
        console.log('[disconnectPlatformAndContinue] ========== STEP 3: CLOSE MODAL ==========')
        this.setMultiPlatformErrorModal({ visible: false })
        console.log('[disconnectPlatformAndContinue] Modal closed')

        // Refresh business settings to reflect disconnected platform
        console.log('[disconnectPlatformAndContinue] ========== STEP 4: REFRESH BUSINESS SETTINGS ==========')
        console.log('[disconnectPlatformAndContinue] Calling fetchBusinessSettings with locationId:', locationId)

        const businessSettingsResponse = await this.fetchBusinessSettings({ locationId })

        console.log('[disconnectPlatformAndContinue] Business settings response received:', !!businessSettingsResponse)
        console.log(
          '[disconnectPlatformAndContinue] Business settings response data:',
          businessSettingsResponse && businessSettingsResponse.data
        )
        console.log('[disconnectPlatformAndContinue] Business settings response structure:', {
          hasResponse: !!businessSettingsResponse,
          hasData: !!(businessSettingsResponse && businessSettingsResponse.data),
          hasLocation: !!(
            businessSettingsResponse &&
            businessSettingsResponse.data &&
            businessSettingsResponse.data.location
          ),
          dataKeys:
            businessSettingsResponse && businessSettingsResponse.data ? Object.keys(businessSettingsResponse.data) : []
        })

        // Refresh social connections to update button state (Connect/Disconnect)
        console.log('[disconnectPlatformAndContinue] ========== STEP 5: REFRESH SOCIAL CONNECTIONS ==========')
        // Get customer ID from response or current state
        let customerId = null

        if (businessSettingsResponse && businessSettingsResponse.data) {
          const settingsData = businessSettingsResponse.data.location || businessSettingsResponse.data
          customerId = settingsData && settingsData.customer
          console.log('[disconnectPlatformAndContinue] Customer ID from response:', customerId)
          console.log('[disconnectPlatformAndContinue] Settings data structure:', {
            hasLocation: !!businessSettingsResponse.data.location,
            hasDirectData: !!(businessSettingsResponse.data && !businessSettingsResponse.data.location),
            settingsDataKeys: settingsData ? Object.keys(settingsData) : []
          })
        }

        // Fallback to current state if not in response
        if (!customerId) {
          console.log('[disconnectPlatformAndContinue] Customer ID not in response, trying current state')
          const currentBusinessSettings =
            rootState && rootState.manage_business && rootState.manage_business.businessSettings
          customerId = currentBusinessSettings && currentBusinessSettings.customer
          console.log('[disconnectPlatformAndContinue] Customer ID from current state:', customerId)
          console.log('[disconnectPlatformAndContinue] Current business settings available:', !!currentBusinessSettings)
          console.log(
            '[disconnectPlatformAndContinue] Current business settings customer:',
            currentBusinessSettings && currentBusinessSettings.customer
          )
        }

        if (customerId) {
          console.log('[disconnectPlatformAndContinue] Customer ID found:', customerId)
          console.log('[disconnectPlatformAndContinue] Calling getSocialConnections with customer ID:', customerId)

          try {
            await this.getSocialConnections({ id: customerId })
            console.log('[disconnectPlatformAndContinue] Social connections refreshed successfully')
          } catch (socialConnectionsError) {
            console.error(
              '[disconnectPlatformAndContinue] ERROR refreshing social connections:',
              socialConnectionsError
            )
            console.error(
              '[disconnectPlatformAndContinue] Social connections error stack:',
              socialConnectionsError.stack
            )
            // Don't throw - this is not critical, button state might still update from business settings
          }
        } else {
          console.warn(
            '[disconnectPlatformAndContinue] WARNING: Customer ID not found, cannot refresh social connections'
          )
          console.warn('[disconnectPlatformAndContinue] Button state may not update until page reload')
        }

        console.log('[disconnectPlatformAndContinue] ========== END DISCONNECT PLATFORM (SUCCESS) ==========')
      } catch (error) {
        console.error('[disconnectPlatformAndContinue] ========== ERROR IN DISCONNECT PLATFORM ==========')
        console.error('[disconnectPlatformAndContinue] Error type:', error.constructor.name)
        console.error('[disconnectPlatformAndContinue] Error message:', error.message)
        console.error('[disconnectPlatformAndContinue] Error stack:', error.stack)
        console.error(
          '[disconnectPlatformAndContinue] Full error object:',
          JSON.stringify(error, Object.getOwnPropertyNames(error), 2)
        )
        notification('error', error.message || 'Failed to disconnect platform. Please try again.')
      }
    },
    getPlatformName(platform) {
      const platformNames = {
        GOOGLE: 'Google Business Profile',
        FACEBOOK: 'Facebook Page',
        google: 'Google Business Profile',
        facebook: 'Facebook Page'
      }
      return platformNames[platform] || platform
    }
  }
}
